import { useState } from "react";
import { motion } from "framer-motion";
import { sendContactForm } from "../services/email";

const contactCards = [
  {
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-cyber-blue" stroke="none">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
      </svg>
    ),
    title: "Visit Us",
    desc: "Nairobi, Kenya",
    detail: "Virtual or on-site consultations available",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-tech-green" stroke="none">
        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
      </svg>
    ),
    title: "Email Us",
    desc: "curiumtech@gmail.com",
    detail: "We respond within 24 hours",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-cyber-blue" stroke="none">
        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
      </svg>
    ),
    title: "Call / WhatsApp",
    desc: "+254 783 058 354",
    detail: "Click-to-chat on WhatsApp",
    isWhatsApp: true,
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" className="w-7 h-7 fill-tech-green" stroke="none">
        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
      </svg>
    ),
    title: "24/7 Support",
    desc: "Technical Assistance",
    detail: "Round-the-clock support for all clients",
  },
];

const highlights = [
  { icon: "🔐", text: "Cybersecurity Solutions" },
  { icon: "🌐", text: "Network Engineering" },
  { icon: "📹", text: "CCTV Systems" },
  { icon: "💻", text: "Web Development" },
];

const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.1 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [formStatus, setFormStatus] = useState(null);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus("sending");
    try {
      await sendContactForm(form);
      setFormStatus("sent");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <section className="w-full py-20 lg:py-28 px-6 lg:px-20 max-w-7xl mx-auto bg-[#0a0e1a]">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-16"
      >
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold">
          Get In{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-tech-green">
            Touch
          </span>
        </h1>
        <p className="text-gray-400 text-lg mt-4 max-w-2xl">
          Have a project in mind? Reach out and let's build something secure,
          scalable, and intelligent.
        </p>
      </motion.div>

      {/* TOP AREA: Contact Info Cards */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-20"
      >
        {contactCards.map((card) => (
          <motion.div
            key={card.title}
            variants={cardVariants}
            whileHover={{ y: -8 }}
            className="glass rounded-2xl p-6 glass-hover hover:border-cyber-blue/30 hover:shadow-[0_0_25px_rgba(0,163,255,0.15)] transition-all duration-300 cursor-default"
          >
            <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-4">
              {card.icon}
            </div>
            <h3 className="text-white font-bold text-lg mb-1">{card.title}</h3>
            {card.isWhatsApp ? (
              <a
                href="https://wa.me/25483058354"
                target="_blank"
                rel="noopener noreferrer"
                title="Chat on WhatsApp"
                className="text-cyber-blue hover:text-tech-green transition-colors text-sm font-medium"
              >
                {card.desc}
              </a>
            ) : card.title === "Email Us" ? (
              <a
                href="mailto:curiumtech@gmail.com"
                className="text-cyber-blue hover:text-tech-green transition-colors text-sm font-medium"
              >
                {card.desc}
              </a>
            ) : (
              <p className="text-gray-300 text-sm font-medium">{card.desc}</p>
            )}
            <p className="text-gray-500 text-xs mt-2">{card.detail}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Social Media Links */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        className="flex flex-wrap items-center justify-center gap-4 mb-16"
      >
        <a
          href="https://www.instagram.com/cariumtech/"
          target="_blank"
          rel="noopener noreferrer"
          title="Visit Instagram"
          className="group flex items-center gap-3 px-5 py-3 glass rounded-xl hover:bg-white/10 hover:border-cyber-blue/30 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(0,163,255,0.2)]"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-cyber-blue group-hover:fill-tech-green transition-colors" stroke="none">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
          </svg>
          <span className="text-sm text-gray-300 group-hover:text-white transition-colors">Instagram</span>
        </a>

        <a
          href="https://www.tiktok.com/@cariumtechnologie"
          target="_blank"
          rel="noopener noreferrer"
          title="Watch TikTok"
          className="group flex items-center gap-3 px-5 py-3 glass rounded-xl hover:bg-white/10 hover:border-cyber-blue/30 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(0,163,255,0.2)]"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5 fill-cyber-blue group-hover:fill-tech-green transition-colors" stroke="none">
            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
          </svg>
          <span className="text-sm text-gray-300 group-hover:text-white transition-colors">TikTok</span>
        </a>
      </motion.div>

      {/* BOTTOM AREA: Split Layout */}
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        {/* LEFT: Heading + Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
            Let&rsquo;s Build Something{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyber-blue to-tech-green">
              Powerful
            </span>
          </h2>
          <p className="text-gray-400 text-lg mt-6 leading-relaxed max-w-lg">
            We design secure networks, software systems, and digital
            infrastructure for modern businesses.
          </p>
          <ul className="mt-8 space-y-4">
            {highlights.map((item) => (
              <li
                key={item.text}
                className="flex items-center gap-3 text-gray-300"
              >
                <span className="text-lg">{item.icon}</span>
                <span className="text-base font-medium">{item.text}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* RIGHT: Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <form onSubmit={handleSubmit} className="glass rounded-2xl p-8 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">
                Your Name
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="John Doe"
                required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-cyber-blue focus:shadow-[0_0_12px_rgba(0,163,255,0.2)] transition-all duration-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="john@example.com"
                required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-cyber-blue focus:shadow-[0_0_12px_rgba(0,163,255,0.2)] transition-all duration-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">
                Subject
              </label>
              <input
                type="text"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="Project inquiry"
                required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-cyber-blue focus:shadow-[0_0_12px_rgba(0,163,255,0.2)] transition-all duration-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1.5">
                Message
              </label>
              <textarea
                name="message"
                rows={4}
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us about your project..."
                required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-cyber-blue focus:shadow-[0_0_12px_rgba(0,163,255,0.2)] transition-all duration-300 resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={formStatus === "sending"}
              className="w-full py-4 bg-cyber-blue text-black font-bold rounded-xl hover:bg-cyber-blue/90 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === "sending" ? "Sending..." : formStatus === "sent" ? "Message Sent!" : "Send Message"}
            </button>
            {formStatus === "sent" && (
              <p className="text-center text-sm text-tech-green">Thanks! We'll get back to you within 24 hours.</p>
            )}
            {formStatus === "error" && (
              <p className="text-center text-sm text-red-400">Something went wrong. Please try again or email us directly.</p>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
}
